// ============================================================================
// offerTemplates.ts — ПРИМЕНЕНИЕ ШАБЛОНА ОТКЛИКА (вертикаль «Маркетплейс»,
// поставка hydra.market). SSoT данных — сервер:
//   • шаблоны — справочник (по умолчанию market_offer_templates: name/
//     template_text/is_active), мастеру — только чтение;
//   • переменные {{Поле}} — реквизиты СВЯЗАННОЙ заявки (поле request_id
//     документа отклика mkt_responses → строка mkt_requests): имя переменной —
//     синоним ИЛИ код колонки, ссылки — серверное представление
//     ({col}_presentation), отсутствующее поле — ПУСТАЯ СТРОКА (решение:
//     шаблон применим даже при неполных данных; исходный {{Поле}} в тексте
//     не остаётся).
// Применение — КЛИЕНТСКОЕ (поставка 1.2.0 явно оставила это за клиентом:
// «текст копируется в «Текст отклика», переменные подставляет клиент»).
// Чтение — через readThrough (офлайн-фёрст: последние сохранённые данные).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { readThrough } from '@/lib/offlineCache';
import { formatValue } from '@/lib/format';

export interface OfferTemplate {
  id: string;
  name: string;
  text: string;
}

/** Настройки вида (переопределяются на элементе формы через settings). */
export interface OfferTemplateSettings {
  /** Код справочника шаблонов */
  catalog: string;
  /** Колонка текста шаблона */
  textField: string;
  /** Колонка-признак «Активен» */
  activeField: string;
  /** Таблица связанной заявки */
  requestTable: string;
  /** Поле-ссылка на заявку в текущей форме */
  requestField: string;
}

export const DEFAULT_OFFER_TEMPLATE_SETTINGS: OfferTemplateSettings = {
  catalog: 'market_offer_templates',
  textField: 'template_text',
  activeField: 'is_active',
  requestTable: 'mkt_requests',
  requestField: 'request_id',
};

/** Настройки вида из extensions элемента формы (camelCase/snake_case). */
export function offerTemplateSettingsFrom(
  extensions?: Array<{ code: string; settings?: Record<string, unknown> }>,
): OfferTemplateSettings {
  const ext = (extensions || []).find((x) => x.code === 'offer_template');
  const s = (ext?.settings || {}) as Record<string, unknown>;
  const pick = (camel: string, snake: string, def: string) => {
    const v = s[camel] ?? s[snake];
    return typeof v === 'string' && v.trim() !== '' ? v.trim() : def;
  };
  return {
    catalog: pick('catalog', 'catalog', DEFAULT_OFFER_TEMPLATE_SETTINGS.catalog),
    textField: pick('textField', 'text_field', DEFAULT_OFFER_TEMPLATE_SETTINGS.textField),
    activeField: pick('activeField', 'active_field', DEFAULT_OFFER_TEMPLATE_SETTINGS.activeField),
    requestTable: pick('requestTable', 'request_table', DEFAULT_OFFER_TEMPLATE_SETTINGS.requestTable),
    requestField: pick('requestField', 'request_field', DEFAULT_OFFER_TEMPLATE_SETTINGS.requestField),
  };
}

/** Строка ListTableData (proto: {id, values:[{column,value}]}) → плоский объект. */
function flattenRow(row: { id?: string; values?: Array<{ column?: string; value?: unknown }> }): Record<string, unknown> {
  const rec: Record<string, unknown> = { id: row?.id || '' };
  for (const v of row?.values || []) {
    if (v?.column) rec[String(v.column)] = v.value;
  }
  return rec;
}

/** Булево значение колонки (PG true / SQLite 1 / строки протокола). */
function isTrue(value: unknown): boolean {
  if (value === true || value === 1) return true;
  const s = String(value ?? '').trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'да';
}

function normalizeVarKey(key: string): string {
  return String(key || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Активные шаблоны отклика (справочник ведёт оператор; мастеру — чтение).
 * Признак «Активен» фильтруется ЗДЕСЬ (значение колонки приходит и как
 * true/false, и как 1/0 — приводим единым правилом).
 */
export async function loadActiveOfferTemplates(
  settings: OfferTemplateSettings = DEFAULT_OFFER_TEMPLATE_SETTINGS,
): Promise<OfferTemplate[]> {
  const { data } = await readThrough<OfferTemplate[]>(`offer-templates:${settings.catalog}`, async () => {
    const res: any = await getSDK().metadata.listTableData({
      schema: 'public', table: settings.catalog, pageSize: 200, page: 0,
    } as any);
    const out: OfferTemplate[] = [];
    for (const raw of (res?.rows || []) as any[]) {
      const rec = flattenRow(raw);
      if (!isTrue(rec[settings.activeField])) continue;
      const text = String(rec[settings.textField] ?? '');
      if (text.trim() === '') continue;
      out.push({
        id: String(raw?.id || rec.id || ''),
        name: String(rec.name || rec.code || '').trim(),
        text,
      });
    }
    out.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    return out;
  });
  return data;
}

export interface OfferTemplateContext {
  requestId: string;
  /** Человеческое имя заявки («Заявка №…»), пусто — не удалось определить. */
  requestLabel: string;
  /** Переменные {{Поле}} (ключ — синоним или код, в нижнем регистре). */
  variables: ReadonlyMap<string, string>;
}

/** Пустой контекст (заявка не выбрана): все переменные — пустые строки. */
export const EMPTY_OFFER_TEMPLATE_CONTEXT: OfferTemplateContext = {
  requestId: '',
  requestLabel: '',
  variables: new Map<string, string>(),
};

interface RequestFieldDef {
  code: string;
  synonym: string;
  dataType: string;
  format: string;
}

function formatScalar(raw: unknown, dataType: string, format: string): string {
  if (raw === null || raw === undefined || raw === '') return '';
  try {
    return formatValue(raw, format || undefined, dataType || undefined);
  } catch {
    return String(raw);
  }
}

/**
 * Переменные шаблона из строки заявки: ключ — КОД колонки и СИНОНИМ поля
 * (по метаданным заявки). Ссылки — представление `{col}_presentation` (SSoT
 * сервера), прочие значения — единый `formatValue` (числа/даты по форматам
 * базы). Пустые значения не попадают в карту (подстановка даст пустую строку).
 */
export function buildOfferTemplateVariables(
  fields: RequestFieldDef[],
  row: Record<string, unknown>,
): Map<string, string> {
  const vars = new Map<string, string>();
  const byCode = new Map<string, string>();
  const set = (key: string, value: string) => {
    const k = normalizeVarKey(key);
    if (k && !byCode.has(k)) byCode.set(k, value);
  };

  for (const [col, raw] of Object.entries(row)) {
    if (col === 'id' || col.endsWith('_presentation')) continue;
    const presentation = row[col + '_presentation'];
    const usePresentation = typeof presentation === 'string' && presentation !== '';
    const def = fields.find((f) => f.code === col);
    const value = usePresentation
      ? String(presentation)
      : formatScalar(raw, def?.dataType || '', def?.format || '');
    if (value !== '') set(col, value);
  }
  // Синонимы реквизитов заявки ({{Заказчик}} и т.п.)
  for (const f of fields) {
    if (!f.synonym) continue;
    const dbKey = normalizeVarKey(f.code);
    set(f.synonym, byCode.get(dbKey) ?? '');
  }
  for (const [k, v] of byCode) if (v !== '') vars.set(k, v);
  return vars;
}

function buildRequestLabel(fields: RequestFieldDef[], row: Record<string, unknown>): string {
  const numberField = fields.find((f) => f.code === 'number' || f.synonym.toLowerCase() === 'номер');
  const number = numberField ? String(row[numberField.code] ?? '') : '';
  return number !== '' ? `Заявка №${number}` : '';
}

/** Контекст шаблона: реквизиты заявки, на которую ссылается поле отклика. */
export async function loadOfferTemplateContext(
  settings: OfferTemplateSettings,
  requestId: string,
): Promise<OfferTemplateContext> {
  if (!requestId) return EMPTY_OFFER_TEMPLATE_CONTEXT;
  const { data } = await readThrough<{ label: string; pairs: Array<[string, string]> }>(
    `offer-template-request:${settings.requestTable}:${requestId}`,
    async () => {
      const rowRes: any = await getSDK().metadata.listTableData({
        schema: 'public', table: settings.requestTable, pageSize: 1, page: 0,
        filters: [{ column: 'id', value: requestId }] as any,
      } as any);
      const raw = ((rowRes?.rows || []) as any[])[0];
      if (!raw) throw new Error('Заявка не найдена — возможно, она удалена или помечена');
      const fieldsRes: any = await getSDK().metadata.listFields({
        entityType: 'document', entityCode: settings.requestTable,
      } as any);
      const fields: RequestFieldDef[] = ((fieldsRes?.items || []) as any[]).map((f) => ({
        code: String(f.code || ''),
        synonym: String(f.synonym || ''),
        dataType: String(f.dataType || f.data_type || ''),
        format: String(f.format || ''),
      }));
      const row = flattenRow(raw);
      return { label: buildRequestLabel(fields, row), pairs: Array.from(buildOfferTemplateVariables(fields, row).entries()) };
    },
  );
  return { requestId, requestLabel: data.label, variables: new Map(data.pairs) };
}

/**
 * Подстановка {{Поле}} в текст шаблона: имя — синоним/код реквизита заявки
 * (регистр и лишние пробелы не важны); НЕИЗВЕСТНОЕ поле → пустая строка.
 */
export function applyOfferTemplate(text: string, variables: ReadonlyMap<string, string>): string {
  return String(text || '').replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_match, key: string) => {
    const value = variables.get(normalizeVarKey(key));
    return value ?? '';
  });
}
